import { useEffect, useState } from 'react'

interface UseTypewriterOptions {
  typeDelay?: number
  deleteDelay?: number
  pauseDelay?: number
}

interface UseTypewriterResult {
  text: string
  phraseIndex: number
}

const DEFAULT_TYPE_DELAY = 70
const DEFAULT_DELETE_DELAY = 50
const DEFAULT_PAUSE_DELAY = 3000

export const useTypewriter = (
  phrases: readonly string[],
  {
    typeDelay = DEFAULT_TYPE_DELAY,
    deleteDelay = DEFAULT_DELETE_DELAY,
    pauseDelay = DEFAULT_PAUSE_DELAY,
  }: UseTypewriterOptions = {},
): UseTypewriterResult => {
  const [phraseIndex, setPhraseIndex] = useState(0)
  const [charIndex, setCharIndex] = useState(0)
  const [isDeleting, setIsDeleting] = useState(false)
  const [isPaused, setIsPaused] = useState(false)
  const [isDocumentVisible, setIsDocumentVisible] = useState(!document.hidden)

  const currentPhrase = phrases[phraseIndex] ?? ''
  const text = currentPhrase.slice(0, charIndex)
  const isLastPhrase = phraseIndex === phrases.length - 1
  const isComplete = isLastPhrase && charIndex === currentPhrase.length && !isDeleting

  useEffect(() => {
    if (phrases.length === 0 || isComplete) {
      return undefined
    }

    if (!isDocumentVisible) {
      return undefined
    }

    let timeoutId: ReturnType<typeof setTimeout>

    if (isPaused) {
      timeoutId = setTimeout(() => {
        setIsPaused(false)
        setIsDeleting(true)
      }, pauseDelay)
    } else if (!isDeleting && charIndex < currentPhrase.length) {
      timeoutId = setTimeout(() => {
        setCharIndex((prev) => prev + 1)
      }, typeDelay)
    } else if (!isDeleting && charIndex === currentPhrase.length) {
      if (!isLastPhrase) {
        setIsPaused(true)
      }
    } else if (isDeleting && charIndex > 0) {
      timeoutId = setTimeout(() => {
        setCharIndex((prev) => prev - 1)
      }, deleteDelay)
    } else if (isDeleting && charIndex === 0) {
      setIsDeleting(false)
      setPhraseIndex((prev) => prev + 1)
    }

    return () => {
      clearTimeout(timeoutId)
    }
  }, [
    charIndex,
    currentPhrase.length,
    deleteDelay,
    isDeleting,
    isPaused,
    pauseDelay,
    phrases.length,
    isDocumentVisible,
    isComplete,
    isLastPhrase,
    typeDelay,
  ])

  useEffect(() => {
    const handleVisibilityChange = () => {
      setIsDocumentVisible(!document.hidden)
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [])

  return { text, phraseIndex }
}
